import React, { useEffect, useState } from "react";
import Sidebar from "../side_bar/SidebarAdmin";
import ProfileForm from "./ProfileFormAdmin";
import avatar from "../../../assets/avatar.png";
import { useAuth } from "../../../context/AuthContext";
import formatPhone from "../../../utils/formatPhone";
import {
  getAdminProfile,
  updateAdminProfile,
} from "../../../api/admin/profile";
import Header from "../../../components/common/Header";
import { AccountPageProps, accountRoleLabel } from "../../../components/common/accountConfig";

interface UserProfileLocal {
  fullName: string;
  phone: string;
  whatsapp: string;
  email: string;
}

const ProfileAdmin: React.FC<AccountPageProps> = ({ role = "Admin" }) => {
  useEffect(() => {
    document.title = "Meu perfil - Insecta";
  }, []);
  const [profileData, setProfileData] = useState<UserProfileLocal>({
    fullName: "",
    phone: "",
    whatsapp: "",
    email: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { getUserIdFromToken, getCsrfToken, user } = useAuth();
  const userId = getUserIdFromToken();

  const clearMessages = () => {
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);
  };
  useEffect(() => {
    const load = async () => {
      try {
        if (!userId) {
          setError("Usuário não autenticado.");
          return;
        }
        const token = getCsrfToken();
        if (!token) {
          setError("Token não encontrado.");
          return;
        }
        const data = await getAdminProfile(userId, token);
        setProfileData({
          fullName: data.nome_completo || "",
          phone: data.telefone
            ? formatPhone(String(data.telefone))
            : "",
          whatsapp: data.whatsapp
            ? formatPhone(String(data.whatsapp))
            : "",
          email: data.email || "",
        });
      } catch (e: any) {
        console.error("Erro ao carregar perfil admin:", e);
        setError("Erro ao carregar dados do perfil.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, getCsrfToken]);

  const handleSave = async (form: UserProfileLocal) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      if (!form.fullName.trim() || !form.email.trim() || !form.phone.trim()) {
        setError("Preencha os campos obrigatórios.");
        return;
      }
      if (!userId) {
        setError("Usuário não autenticado.");
        return;
      }
      const token = getCsrfToken();
      if (!token) {
        setError("Token não encontrado.");
        return;
      }
      const updateData: any = {
        nome_completo: form.fullName.trim(),
        telefone: form.phone.replace(/\D/g, ""),
        email: form.email.trim(),
      };
      if (form.whatsapp && form.whatsapp !== "Informe seu número do WhatsApp") {
        updateData.whatsapp = form.whatsapp.replace(/\D/g, "");
      }
      const updated = await updateAdminProfile(userId, updateData, token);
      setProfileData({
        fullName: updated.nome_completo || form.fullName,
        phone: updated.telefone
          ? formatPhone(String(updated.telefone))
          : form.phone,
        whatsapp: updated.whatsapp
          ? formatPhone(String(updated.whatsapp))
          : form.whatsapp,
        email: updated.email || form.email,
      });
      setSuccess("Perfil atualizado com sucesso!");
      clearMessages();
    } catch (err: any) {
      console.error("Erro ao salvar perfil admin:", err);
      setError("Erro ao atualizar perfil.");
      clearMessages();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setError("");
    setSuccess("");
  };

  if (loading) {
    return (
      <div className="container-fluid text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="account-profile-page">
      <Header />
      {/* Botão 'Voltar para busca' removido (agora na sidebar) */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}
      {success && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
          ></button>
        </div>
      )}
      <div className="account-profile-layout">
        <div className="account-navigation">
          <Sidebar
            role={role}
            activeItem="perfil"
            avatar={avatar}
            name={
              profileData.fullName !== "Digite seu nome completo"
                ? profileData.fullName
                : user?.nome_completo || "Administrador"
            }
          />
        </div>
        <main className="account-profile-content">
          <div className="account-page-heading">
            <h1>Meu perfil</h1>
          </div>
          {saving && (
            <div
              className="alert alert-info d-flex align-items-center"
              role="alert"
            >
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></div>
              Salvando alterações...
            </div>
          )}
          <ProfileForm
            profile={profileData}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
            profileType={accountRoleLabel(role)}
          />
        </main>
      </div>
    </div>
  );
};

export default ProfileAdmin;
